import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";

const RESEND_CONFIRMATION_WITH_TOKEN = gql`
  mutation ResendConfirmationWithToken($email: String!, $confirmUrl: String) {
    resendConfirmationWithToken(email: $email, confirmUrl: $confirmUrl) {
      success
      errors
      message
      alreadySent
    }
  }
`

type ResendConfirmationWithTokenResp = { resendConfirmationWithToken: {
    errors: string[]
    alreadySent: boolean
    message: string
    success: boolean
  }
}

type ResendConfirmationWithTokenVariables = {
  email: string
  confirmUrl?: string
}

type UseResendConfirmationWithTokenOptions = {
  onCompleted?: (params: {success: boolean, message: string, alreadySent: boolean}) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<ResendConfirmationWithTokenResp, ResendConfirmationWithTokenVariables>>[1];
};

const useResendConfirmationWithToken = (options?: UseResendConfirmationWithTokenOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    ResendConfirmationWithTokenResp,
    ResendConfirmationWithTokenVariables
  >(RESEND_CONFIRMATION_WITH_TOKEN, options?.mutationOptions);

  const resendConfirmationWithToken = useCallback(
    async (input: ResendConfirmationWithTokenVariables) => {
      try {
        const result = await mutate({
          variables: input
        });

        const resendConfirmationWithToken = result?.data?.resendConfirmationWithToken;

        if (!resendConfirmationWithToken) {
          throw new Error("No response from server");
        }

        if (resendConfirmationWithToken?.errors?.length) {
          throw new Error(
            resendConfirmationWithToken.errors.join(", ")
          );
        }

        const {success, message, alreadySent} = resendConfirmationWithToken
        if (options?.onCompleted && resendConfirmationWithToken?.success) {
          options.onCompleted({
            success,
            message,
            alreadySent
          });
        }

        return {
            success,
            message,
            alreadySent
          };
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const { success, message, alreadySent } = data?.resendConfirmationWithToken || {};

  return {
    resendConfirmationWithToken,
    response: { success, message, alreadySent },
    loading,
    error: error as Error | null | undefined,
  };
}

export default useResendConfirmationWithToken
